import type { ReactNode } from "react";
import { mapboxStaticStylePath } from "./mapboxStyle";

const MAPBOX_TOKEN =
  "pk.eyJ1IjoiY2hyaXN0b3BoZXJzdG90dCIsImEiOiJjazdmZnZ1eDIwMmR0M25wcnMxOWMzNTIzIn0.LBCxe4yd1d0SohHTSvzSmg";

interface StaticMapProps {
  latitude: number;
  longitude: number;
  zoom?: number;
  width?: number;
  height?: number;
  /**
   * Show a placeholder instead of the map. Use while the coordinates are still
   * being resolved, so the space doesn't collapse and then jump.
   */
  loading?: boolean;
  /** Make the map a link, opening in a new tab. Without it the map is inert. */
  href?: string;
}

function Frame_({
  href,
  children,
}: {
  href: string | undefined;
  children: ReactNode;
}) {
  const className = "block overflow-hidden border border-border bg-card";
  if (href === undefined) {
    return <div className={className}>{children}</div>;
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
    >
      {children}
    </a>
  );
}

export function StaticMap({
  latitude,
  longitude,
  zoom = 4,
  width = 450,
  height = 160,
  loading,
  href,
}: StaticMapProps) {
  const style = mapboxStaticStylePath();
  // It drops an accent-coloured pin on the colour map.
  const pin = "pin-s+4c62ea";
  const url = `https://api.mapbox.com/styles/v1/${style}/static/${pin}(${longitude},${latitude})/${longitude},${latitude},${zoom}/${width}x${height}?access_token=${MAPBOX_TOKEN}`;

  if (loading) {
    return (
      <div
        className="animate-pulse overflow-hidden border border-border bg-muted"
        style={{ height: `${height}px` }}
        data-testid="static-map-loading"
      />
    );
  }

  return (
    <Frame_ href={href}>
      <img
        src={url}
        alt="Location map"
        data-testid="static-map-image"
        className="w-full"
        style={{ height: `${height}px`, objectFit: "cover" }}
      />
    </Frame_>
  );
}
